/**
 * CHARMS Cart Sync Listener
 *
 * Purpose: Sync CHARMS app cart with Shopify theme cart
 * How it works: Listens for postMessage from iframe, uses Ajax Cart API, refreshes drawer
 * Integration: Add to theme as snippet or app embed
 * Configuration: Update ALLOWED_ORIGINS for production
 * Note: Duplicate prevention on back-navigation should be handled app-side by clearing local cart after checkout
 */

// Configuration Section
const CART_SYNC_REQUEST = 'CART_SYNC_REQUEST';
const CART_SYNC_SUCCESS = 'CART_SYNC_SUCCESS';
const CART_SYNC_ERROR = 'CART_SYNC_ERROR';

// Allowed iframe origins - update for production deployment
// Example: ['https://your-app-domain.com', 'https://staging.your-app-domain.com']
const ALLOWED_ORIGINS = ['https://wolf-charmsapp.vercel.app', 'http://localhost:5174', 'http://localhost:5173','http://localhost:4000'];

const DEBUG_MODE = false;

// Cart drawer section IDs to refresh
const CART_DRAWER_SECTIONS = ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'];

const ENABLE_FALLBACK_RELOAD = false;

/**
 * Get the Shopify routes root with fallback
 * @returns {string} The routes root path
 */
function getShopifyRoutesRoot() {
  if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
    return window.Shopify.routes.root;
  }
  console.warn('[CHARMS Cart Sync] window.Shopify.routes.root not available, using fallback');
  return window.location.origin + '/';
}

/**
 * Validates if the message origin is allowed
 * @param {string} origin - The origin of the message
 * @returns {boolean} True if origin is allowed or allowlist is empty (development mode)
 */
function isAllowedOrigin(origin) {
  // Allow all origins if allowlist is empty (development mode with wildcard)
  if (!ALLOWED_ORIGINS || ALLOWED_ORIGINS.length === 0) {
    return true;
  }
  
  const isAllowed = ALLOWED_ORIGINS.includes(origin);
  if (!isAllowed) {
    console.warn('[CHARMS Cart Sync] Rejected message from unauthorized origin:', origin);
  }
  return isAllowed;
}

/**
 * Removes app items from the cart before adding new ones
 * Identifies items by Position property (addon charms) or base variant IDs (bracelets)
 * @returns {Promise<number>} Number of items removed
 */
async function removeAppItemsFromCart() {
  try {
    const routesRoot = getShopifyRoutesRoot();
    const cartUrl = routesRoot + 'cart.js';
    const response = await fetch(cartUrl);

    if (!response.ok) {
      console.warn('[CHARMS Cart Sync] Failed to fetch cart for removal, skipping');
      return 0;
    }

    const cart = await response.json();
    if (!cart || !cart.items || !Array.isArray(cart.items)) {
      return 0;
    }

    const itemsToRemove = [];
    cart.items.forEach(item => {
      if (item.properties && item.properties._charmsApp === 'true') {
        itemsToRemove.push(item.key);
      }
    });

    if (itemsToRemove.length === 0) {
      return 0;
    }

    const updates = {};
    itemsToRemove.forEach(key => {
      updates[key] = 0;
    });

    const updateUrl = routesRoot + 'cart/update.js';
    const updateResponse = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });

    if (!updateResponse.ok) {
      console.warn('[CHARMS Cart Sync] Failed to remove app items, status:', updateResponse.status);
      return 0;
    }

    console.log(`[CHARMS Cart Sync] Removing ${itemsToRemove.length} app items (base products + positioned addons) from cart`);
    return itemsToRemove.length;
  } catch (error) {
    console.warn('[CHARMS Cart Sync] Error removing app items from cart:', error);
    return 0;
  }
}

/**
 * Adds items to the Shopify cart via Ajax Cart API
 * @param {Array} items - Array of items to add, formatted for Shopify Ajax API
 * @returns {Promise<Object>} Response containing cart data and sections HTML
 * @throws {Error} If the request fails or returns an error
 */
async function addItemsToShopifyCart(items) {
  if (!items || !Array.isArray(items) || items.length === 0) {
    throw new Error('No items provided to add to cart');
  }

  await removeAppItemsFromCart();

  const routesRoot = getShopifyRoutesRoot();
  const url = routesRoot + 'cart/add.js';
  const sectionsParam = CART_DRAWER_SECTIONS.join(',');
  const sectionsUrl = window.location.pathname;

  const requestBody = {
    items: items,
    sections: sectionsParam,
    sections_url: sectionsUrl
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      if (response.status === 422) {
        throw new Error('Invalid variant IDs or cart validation error');
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    // Validate response structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response format from cart API');
    }

    return data;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new Error('Network error: Unable to connect to Shopify cart API');
    }
    throw error;
  }
}

/**
 * Refreshes the cart drawer UI with updated sections
 * @param {Object} sections - Sections object from Ajax Cart API response
 */
function refreshCartDrawer(sections) {
  if (!sections || typeof sections !== 'object') {
    console.warn('[CHARMS Cart Sync] No sections provided for cart drawer refresh');
    return;
  }

  const updatedSections = [];
  const missingSections = [];

  CART_DRAWER_SECTIONS.forEach(sectionId => {
    const sectionHtml = sections[sectionId];
    if (!sectionHtml) {
      missingSections.push(sectionId);
      return;
    }

    // Try multiple selector strategies to find the DOM container
    let container = document.getElementById('shopify-section-' + sectionId);
    if (!container) {
      container = document.querySelector('[data-section-id="' + sectionId + '"]');
    }
    if (!container) {
      container = document.querySelector('#' + sectionId);
    }

    if (container) {
      container.innerHTML = sectionHtml;
      updatedSections.push(sectionId);
    } else {
      missingSections.push(sectionId);
    }
  });

  // Trigger theme-specific refresh hooks
  try {
    // Dawn theme compatibility
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents();
    }
    
    // Dispatch custom event for theme listeners
    window.dispatchEvent(new CustomEvent('cart:updated'));
  } catch (error) {
    console.warn('[CHARMS Cart Sync] Error triggering cart refresh hooks:', error);
  }

  if (updatedSections.length === 0) {
    window.dispatchEvent(new CustomEvent('cart:force-refresh'));
    if (ENABLE_FALLBACK_RELOAD) {
      setTimeout(() => { window.location.reload(); }, 1000);
    }
    console.warn('[CHARMS Cart Sync] No sections updated, dispatched force-refresh event');
  }

  console.log('[CHARMS Cart Sync] Cart drawer refresh completed:', {
    updated: updatedSections,
    missing: missingSections
  });
}

/**
 * Sends a response message back to the iframe
 * @param {HTMLIFrameElement} iframe - The iframe element to send response to
 * @param {string} type - Response type (CART_SYNC_SUCCESS or CART_SYNC_ERROR)
 * @param {Object} data - Response data
 * @param {string} targetOrigin - The origin to send the message to (defaults to '*')
 */
function sendResponseToIframe(iframe, type, data, targetOrigin = '*') {
  if (!iframe || !iframe.contentWindow) {
    console.error('[CHARMS Cart Sync] Iframe not available for response');
    return;
  }

  const message = {
    type: type,
    data: data,
    timestamp: Date.now(),
    source: 'shopify-parent'
  };

  try {
    iframe.contentWindow.postMessage(message, targetOrigin);
    console.log('[CHARMS Cart Sync] Response sent to iframe at origin:', targetOrigin);
  } catch (error) {
    console.error('[CHARMS Cart Sync] Error sending response to iframe:', error);
  }
}

/**
 * Handles incoming cart sync messages from the iframe
 * @param {MessageEvent} event - The message event
 */
async function handleCartSyncMessage(event) {
  // addItemsToShopifyCart will remove app items first (items with _charmsApp marker), then add new items. Items from collection pages are preserved. This allows multiple charms with same variant ID on different positions, and allows users to navigate back from checkout to continue configuring their bracelet.
  const replyOrigin = event.origin;
  try {
    // Validation Phase
    if (!event.data || typeof event.data !== 'object') {
      return;
    }

    const { type, data, source } = event.data;

    // Handle REQUEST_CHECKOUT (no source validation needed)
    if (type === 'REQUEST_CHECKOUT') {
      console.log('[CHARMS Cart Sync] Received REQUEST_CHECKOUT, navigating to checkout');
      const routesRoot = getShopifyRoutesRoot();
      const checkoutUrl = routesRoot + 'checkout';
      console.log('[CHARMS Cart Sync] Navigating to:', checkoutUrl);
      window.location.assign(checkoutUrl);
      return;
    }

    // Only process CART_SYNC_REQUEST from here on
    if (type !== CART_SYNC_REQUEST) {
      return;
    }

    // Validate source for CART_SYNC_REQUEST (silently ignore if wrong)
    if (source !== 'charms-iframe') {
      return;
    }

    if (!isAllowedOrigin(event.origin)) {
      return;
    }

    if (!data || !data.items || !Array.isArray(data.items) || data.items.length === 0) {
      console.warn('[CHARMS Cart Sync] No items provided in cart sync request');
      return;
    }

    // Valid cart sync request received
    console.log('[CHARMS Cart Sync] Valid cart sync request received from iframe');

    // Processing Phase
    if (DEBUG_MODE) {
      console.log('[CHARMS Cart Sync] Items being sent to Shopify API:', data.items);
    }
    console.log('[CHARMS Cart Sync] Processing cart sync request with', data.items.length, 'items');

    const response = await addItemsToShopifyCart(data.items);
    if (DEBUG_MODE) {
      console.log('[CHARMS Cart Sync] Response from Shopify API:', response);
    }
    const sections = response.sections || {};

    refreshCartDrawer(sections);

    // Find the iframe that sent the message
    const iframes = document.querySelectorAll('iframe');
    let targetIframe = null;
    for (const iframe of iframes) {
      try {
        if (iframe.contentWindow === event.source) {
          targetIframe = iframe;
          break;
        }
      } catch (error) {
        // Cross-origin iframe, skip
      }
    }

    if (targetIframe) {
      console.log('[CHARMS Cart Sync] Cart drawer updated successfully');

      sendResponseToIframe(targetIframe, CART_SYNC_SUCCESS, {
        itemCount: data.items.length,
        cartItemCount: response.items?.length || 0
      }, replyOrigin);
    } else {
      console.warn('[CHARMS Cart Sync] Could not find target iframe for response');
    }

  } catch (error) {
    console.error('[CHARMS Cart Sync] Error processing cart sync:', error.message, error.stack);

    // Find the iframe for error response
    const iframes = document.querySelectorAll('iframe');
    let targetIframe = null;
    for (const iframe of iframes) {
      try {
        if (iframe.contentWindow === event.source) {
          targetIframe = iframe;
          break;
        }
      } catch (error) {
        // Cross-origin iframe, skip
      }
    }

    if (targetIframe) {
      sendResponseToIframe(targetIframe, CART_SYNC_ERROR, {
        error: error.message || 'Unknown error occurred'
      }, replyOrigin);
    }
  }
}

// Initialization
// This script should be added to the Shopify theme before the closing </body> tag
window.addEventListener('message', handleCartSyncMessage);
console.log('[CHARMS Cart Sync] CHARMS Cart Sync Listener initialized');